import { Card, Switch, Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { trackUserEvent } from '@vend/utilities'
import React, { ChangeEvent, FunctionComponent, useEffect, useRef } from 'react'
import { FEATURE_COMPLETE_PICKUP_AFTER_PAYMENT } from '../../../constants/wr-features'
import { hasOutstandingBalance, isOnAccount } from '../../../functions/sale'
import { FulfilmentType } from '../../../models/fulfilment'
import { ReceiptNumberDetails } from '../../../models/sale'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCurrentSale,
  selectLineItems,
  selectSaleId,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useIsOffline } from '../../shared/useIsOffline'
import { WEB_REGISTER } from '../../web-register.constant'

const TOGGLE_CONTEXT =
  'Label for mark as picked up toggle on payment confirmation'

const getOrderLabel = (
  receiptNumberDetails: ReceiptNumberDetails | string | undefined,
  pickupItemCount: number
) => {
  let orderNumber: string | undefined

  if (typeof receiptNumberDetails === 'string') {
    orderNumber = receiptNumberDetails
  } else if (receiptNumberDetails) {
    orderNumber =
      receiptNumberDetails.prefix +
      receiptNumberDetails.number +
      receiptNumberDetails.suffix
  }

  if (orderNumber) {
    return (
      <T
        _str="{count, plural, one {Order #{orderNumber} | {count} item} other {Order #{orderNumber} | {count} items}}"
        _context={TOGGLE_CONTEXT}
        orderNumber={orderNumber}
        count={pickupItemCount}
      />
    )
  }

  return (
    <T
      _str="{count, plural, one {{count} item} other {{count} items}}"
      _context={TOGGLE_CONTEXT}
      count={pickupItemCount}
    />
  )
}

interface MarkAsPickedUpToggleProps {
  isPickedUp: boolean
  setIsPickedUp: (value: boolean) => void
}

export const MarkAsPickedUpToggle: FunctionComponent<MarkAsPickedUpToggleProps> = ({
  isPickedUp,
  setIsPickedUp,
}) => {
  const currentSale = useAppSelector(selectCurrentSale)
  const saleId = useAppSelector(selectSaleId)
  const lineItems = useAppSelector(selectLineItems)
  const { isOffline } = useIsOffline()
  const hasTrackedRef = useRef<string | undefined>(undefined)

  const featureEnabled = getFeatureFromCache(
    FEATURE_COMPLETE_PICKUP_AFTER_PAYMENT
  )

  const isContinuedSale =
    Boolean(saleId) &&
    (currentSale.isSavedUnfulfilled || isOnAccount(currentSale.status))
  const unfulfiledPickupItems = lineItems.filter(
    li => li.fulfilmentType === FulfilmentType.PICKUP && !li.fulfilmentCompleted
  )
  const hasUnfulfiledPickupItems = unfulfiledPickupItems.length > 0
  const unfulfiledPickupItemCount = unfulfiledPickupItems.reduce(
    (sum, li) => sum + (Number(li.quantity) || 0),
    0
  )

  const shouldShow =
    featureEnabled &&
    isContinuedSale &&
    !hasOutstandingBalance(currentSale) &&
    hasUnfulfiledPickupItems &&
    !isOffline

  useEffect(() => {
    if (shouldShow && hasTrackedRef.current !== saleId) {
      hasTrackedRef.current = saleId
      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Pickup Toggle Shown', {
        saleId,
        status: currentSale.status,
      })
    }
  }, [shouldShow, saleId, currentSale.status])

  useEffect(() => {
    if (!shouldShow) {
      setIsPickedUp(false)
    }
  }, [shouldShow, setIsPickedUp])

  if (!shouldShow) {
    return null
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsPickedUp(e.target.checked)
  }

  return (
    <Card className="vd-mt10" data-testid="mark-as-picked-up-toggle">
      <label className="vd-ma5 vd-flex">
        <div className="vd-mr4">
          <Switch small={true} checked={isPickedUp} onChange={handleChange} />
        </div>
        <div className="vd-align-left">
          <div className="vd-mb2">
            <Text>
              <T _str="Mark as picked up" _context={TOGGLE_CONTEXT} />
            </Text>
          </div>
          <div className="vd-text--secondary vd-text--sub">
            {getOrderLabel(
              currentSale.receiptNumberDetails,
              unfulfiledPickupItemCount
            )}
          </div>
        </div>
      </label>
    </Card>
  )
}
